import useGlotaT from 'src/hooks/useGlotaTranslation';
import { ChannelUsage, ExtraUsage, GroupUsage } from '../CreditsCard';
import styles from './OldCreditsCard.module.css';
import { Icon, Button } from '@polichat/flamboyant';
import Tippy from '@tippyjs/react';

interface MappedExtraUsage extends ExtraUsage {
  translatedLabel: string;
}

interface OldCardProps {
  billingAi: boolean;
  channelTitle: string;
  channels: ChannelUsage[];
  onBuy?: () => void;
  newBillingModel: boolean;
  detachedCreditsTotal: number;
  groups?: Record<string, GroupUsage>;
  availableDisplayed: number;
  totalConsumed: number;
  percentDisplay: number;
  mappedExtras: MappedExtraUsage[];
}

export const OldCard = ({
  billingAi,
  channelTitle,
  channels,
  onBuy,
  newBillingModel,
  detachedCreditsTotal,
  groups,
  availableDisplayed,
  totalConsumed,
  percentDisplay,
  mappedExtras,
}: OldCardProps) => {
  const creditsConsumptionText = useGlotaT('spa.tools.creditsConsumption');
  const availableCreditsText = useGlotaT('spa.tools.availableCredits');
  const consumedThisCycleText = useGlotaT('spa.tools.consumedThisCycle');
  const consumptionThisCycleText = useGlotaT('spa.tools.consumptionThisCycle');
  const consumedText = useGlotaT('spa.tools.consumed');
  const buyCreditsText = useGlotaT('spa.tools.buyCredits');
  const wabaAccountText = useGlotaT('spa.tools.wabaAccount');
  const aiIncludesText = useGlotaT('spa.tools.aiIncludesTranscripts');

  const formatNumber = (n: number) =>
    n?.toLocaleString('pt-BR', { minimumFractionDigits: 0 });
  return (
    <div className={styles.card}>
      <h3 className={styles.creditsBoxTitle}>{creditsConsumptionText}</h3>

      <div className={styles.creditsBox}>
        <div className={styles.header}>
          <span className={styles.label}>{availableCreditsText}</span>
          <span className={`${styles.value} ${styles.availableCredits}`}>
            {formatNumber(availableDisplayed || 0)}
          </span>
        </div>

        <hr className={styles.divider} />

        <div className={styles.body}>
          <div className={styles.consumedLine}>
            <strong>{consumedThisCycleText}</strong>
            <br />
            <strong>
              {`${formatNumber(totalConsumed)} / ${formatNumber(
                detachedCreditsTotal
              )}`}
            </strong>
          </div>

          <div className={styles.progressBar}>
            <div
              className={styles.progress}
              style={{ width: `${percentDisplay}%` }}
            />
          </div>

          <div className={styles.percentText}>
            {`${percentDisplay.toFixed(1)}% ${consumedText}`}
          </div>
        </div>
      </div>

      <div className={styles.channelsSection}>
        <h4 className={styles.channelsTitle}>{consumptionThisCycleText}</h4>

        <div className={styles.channelsHeader}>
          <span className={styles.channelsHeaderLabel}>{channelTitle}</span>
          <span className={styles.channelsHeaderValue}>{channels.length}</span>
        </div>

        <ul className={styles.channelList}>
          {newBillingModel ? (
            <>
              {channels.map((ch) => (
                <li key={ch.label} className={styles.channelItem}>
                  {typeof ch.icon === 'string' ? (
                    <Icon icon={ch.icon} className={styles.channelIcon} />
                  ) : (
                    <span className={styles.channelIcon}>{ch.icon}</span>
                  )}
                  <span className={styles.channelLabel}>{ch.label}</span>
                  <span className={styles.channelValue}>
                    {formatNumber(ch.value)}
                  </span>
                </li>
              ))}
            </>
          ) : (
            <>
              {groups && Object.values(groups).length === 0 && (
                <li className={styles.channelItem}>
                  <span className={styles.channelIcon}>
                    <Icon
                      icon="poli-icon pi-whatsapp-api-fill"
                      className={styles.channelIcon}
                    />
                  </span>
                  <span className={styles.channelLabel}>{wabaAccountText}</span>
                  <span className={styles.channelValue}>0/0</span>
                </li>
              )}

              {groups &&
                Object.values(groups).map((group) => {
                  const displayUsage = `${formatNumber(
                    group.groupUsed
                  )}/${formatNumber(group.groupTotal)}`;

                  return (
                    <li
                      key={group.groupId}
                      className={styles.channelItem}
                      style={{ flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '8px',
                        }}
                      >
                        <span className={styles.channelIcon}>
                          <Icon
                            icon="poli-icon pi-whatsapp-api-fill"
                            className={styles.channelIcon}
                          />
                        </span>
                        <span
                          className={styles.channelLabel}
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {group.wabaName}
                        </span>
                        <span
                          style={{ marginLeft: 'auto' }}
                          className={styles.channelValue}
                        >
                          {displayUsage}
                        </span>
                      </div>

                      <ul
                        className={`${styles.channelList} ${
                          styles.channelSubList ? styles.channelSubList : ''
                        }`}
                      >
                        {group.channels.map((ch) => (
                          <li key={ch.label} className={styles.channelItem}>
                            <span className={styles.channelIcon}></span>
                            <span
                              className={styles.channelLabel}
                              style={{ fontSize: '13px' }}
                            >
                              {ch.label}
                            </span>
                            <span className={styles.channelValue}>
                              {formatNumber(ch.value)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
            </>
          )}
        </ul>
      </div>

      <hr className={styles.secondaryDivider} />
      <ul className={styles.extraList}>
        {mappedExtras.map((item) => (
          <li key={item.translatedLabel} className={styles.extraItem}>
            <div className={styles.extraLeft}>
              {typeof item.icon === 'string' ? (
                <Icon icon={item.icon} className={styles.extraIcon} />
              ) : (
                <span className={styles.extraIcon}>{item.icon}</span>
              )}
              <span className={styles.extraLabel}>{item.translatedLabel}</span>
              {item.info && (
                <>
                  <Tippy
                    theme="dark"
                    placement="right"
                    trigger="mouseenter"
                    hideOnClick={false}
                    appendTo={() => document.body}
                    interactive
                    arrow={true}
                    content={
                      <div style={{ maxWidth: '212px', whiteSpace: 'normal' }}>
                        {aiIncludesText}
                      </div>
                    }
                  >
                    <span>
                      <Icon
                        icon="poli-icon pi-info-fill"
                        className={styles.infoIcon}
                      />
                    </span>
                  </Tippy>
                </>
              )}
            </div>
            {typeof item.value === 'number' && (
              <span className={styles.extraValue}>
                {formatNumber(item.value)}
              </span>
            )}
          </li>
        ))}
      </ul>
      <hr />
      <Button color="primary" className={styles.buyButton} onClick={onBuy}>
        {buyCreditsText}
      </Button>
    </div>
  );
};
